import React, { useEffect, useState, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import secureAxios from "../../utils/secureAxios";
import JobLogForm from "./JobLogForm";
import { Tabs, Tab, Alert } from "react-bootstrap";
import { AuthContext } from "../../contexts/AuthContext";
import { formatDate } from "../../utils/dateFormatter";

// Privacy Notice Component (exactly like MileagePrivacyNotice)
const JobLogPrivacyNotice = ({ onDismiss }) => (
  <div className="alert alert-info alert-dismissible fade show mb-3">
    <button type="button" className="btn-close" onClick={onDismiss}></button>
    <h6 className="alert-heading">
      <i className="bi bi-shield-lock me-2"></i>Protected Health Information
      Notice
    </h6>
    <small>
      This system contains Protected Health Information (PHI). All access is
      tracked for HIPAA compliance.
    </small>
  </div>
);

// HIPAA Footer Component
const HIPAAFooter = () => (
  <div className="text-center text-muted small mt-5 mb-3">
    <hr />
    <p className="mb-2">
      <i className="bi bi-shield-check me-1"></i>
      This system is HIPAA-compliant and all activities are monitored.
    </p>
    <p>
      Access to PHI must be limited to the minimum necessary for authorized work
      purposes. Report any suspected security incidents immediately.
    </p>
  </div>
);

const JobLogPage = () => {
  const [key, setKey] = useState("list");
  const [jobLogs, setJobLogs] = useState([]);
  const { user } = useContext(AuthContext);
  const tenantId = user?.tenant_id;
  const [editingLog, setEditingLog] = useState(null);
  const [selectedLogIds, setSelectedLogIds] = useState([]);
  const [consumers, setConsumers] = useState([]);
  const [authorizations, setAuthorizations] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [prefillData, setPrefillData] = useState(null);
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    is_paid: "",  // Show all logs regardless of paid status
    is_billable: "",
    consumer_search: "",
    authorization_search: "",
    coach_search: "",
  });
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    variant: "info",
  });
  const [showPrivacyNotice, setShowPrivacyNotice] = useState(() => {
    // Check if user has dismissed the notice in this session
    return sessionStorage.getItem("jobLogPrivacyNoticeDismissed") !== "true";
  });

  const fetchJobLogs = async () => {
    try {
      // Only send filter parameters, not user/tenant info (backend gets from auth)
      const query = new URLSearchParams({
        ...(filters.start_date && { start_date: filters.start_date }),
        ...(filters.end_date && { end_date: filters.end_date }),
        ...(filters.is_paid && { is_paid: filters.is_paid }),
        ...(filters.is_billable && { is_billable: filters.is_billable }),
        ...(filters.consumer_search && {
          consumer_name: filters.consumer_search,
        }),
        ...(filters.authorization_search && {
          authorization_search: filters.authorization_search,
        }),
        ...(filters.coach_search && { user_name: filters.coach_search }),
      }).toString();

      console.log("Fetching job logs with filters:", filters);

      const res = await secureAxios.get(
        `/api/job-logs${query ? "?" + query : ""}`
      );
      console.log("Job logs response:", res.data);
      setJobLogs(res.data);

      // Clear any existing error alerts
      setAlert({
        show: false,
        message: "",
        variant: "info",
      });
    } catch (err) {
      console.error("Error fetching job logs:", err);
      console.error("Error response:", err.response);
      console.error("Error status:", err.response?.status);
      console.error("Error data:", err.response?.data);

      setAlert({
        show: true,
        message: "Failed to load job logs. Please refresh the page.",
        variant: "danger",
      });
    }
  };

  useEffect(() => {
    if (tenantId && user?.user_id && key === "list") {
      fetchJobLogs();
    }
  }, [filters, tenantId, user?.user_id, key]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this job log? This action cannot be undone."))
      return;
    try {
      await secureAxios.delete(`/api/job-logs/${id}`);
      fetchJobLogs();
      setAlert({
        show: true,
        message: "[OK] Job log deleted successfully.",
        variant: "success",
      });
    } catch (err) {
      console.error("Error deleting log:", err);
      setAlert({
        show: true,
        message: "[X] Failed to delete job log.",
        variant: "danger",
      });
    }
  };

  useEffect(() => {
    const consumerId = searchParams.get("consumer_id");
    const date = searchParams.get("date");
    const userId = searchParams.get("user_id");

    if (consumerId) {
      // Ensure date is in yyyy-MM-dd format (strip time portion if present)
      let formattedDate = new Date().toISOString().split("T")[0];
      if (date) {
        // Handle both "2025-12-30" and "2025-12-30T05:00:00.000Z" formats
        formattedDate = date.includes("T") ? date.split("T")[0] : date;
      }

      setPrefillData({
        consumer_id: consumerId,
        date: formattedDate,
        user_id: userId || null, // Pass user_id (job coach) if provided
      });
      setKey("form");

      // Clear the URL params after reading them
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const toggleSelectLog = (log_id) => {
    setSelectedLogIds((prev) =>
      prev.includes(log_id)
        ? prev.filter((id) => id !== log_id)
        : [...prev, log_id]
    );
  };

  const handleGeneratePDF = async () => {
    if (selectedLogIds.length === 0) {
      setAlert({
        show: true,
        message: "Select at least one log to generate PDF",
        variant: "warning",
      });
      return;
    }

    try {
      const res = await secureAxios.post(
        "/api/pdf/job-log-preview",
        {
          tenant_id: tenantId,
          user_id: user.user_id,
          selected_log_ids: selectedLogIds,
        },
        { responseType: "blob" }
      );

      const pdfBlob = new Blob([res.data], { type: "application/pdf" });
      const pdfURL = window.URL.createObjectURL(pdfBlob);
      window.open(pdfURL, "_blank");
    } catch (err) {
      console.error("[X] Failed to generate PDF:", err);
      setAlert({
        show: true,
        message: "Failed to generate PDF. Please try again.",
        variant: "danger",
      });
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  // Auto-hide alert after 5 seconds
  useEffect(() => {
    if (alert.show) {
      const timer = setTimeout(() => {
        setAlert({ ...alert, show: false });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [alert.show]);

  // Load consumers for filter dropdown
  useEffect(() => {
    if (user?.tenant_id) {
      const fetchConsumers = async () => {
        try {
          const res = await secureAxios.get(`/api/consumers`, {
            params: {
              tenant_id: user.tenant_id,
              user_id: user.user_id,
              role: user.role,
              tenant_type: user.tenant_type,
            },
          });
          setConsumers(res.data);
        } catch (err) {
          console.error("Error loading consumers for filter:", err);
        }
      };
      fetchConsumers();
    }
  }, [user]);

  // Load authorizations for filter dropdown
  useEffect(() => {
    if (user?.tenant_id) {
      const fetchAuthorizations = async () => {
        try {
          const res = await secureAxios.get(`/api/auths`, {
            params: {
              tenant_id: user.tenant_id,
              active: true,
            },
          });
          setAuthorizations(res.data);
        } catch (err) {
          console.error("Error loading authorizations for filter:", err);
        }
      };
      fetchAuthorizations();
    }
  }, [user]);

  // Callback for when job log is saved in form
  const handleJobLogSaved = () => {
    // Switch to list tab
    setKey("list");
    setEditingLog(null);
    setPrefillData(null);
    fetchJobLogs();
  };

  const handleDismissPrivacyNotice = () => {
    setShowPrivacyNotice(false);
    sessionStorage.setItem("jobLogPrivacyNoticeDismissed", "true");
  };

  // Check if any logs contain PHI (exactly like mileage page)
  const hasPhiData = jobLogs.some((log) => log.consumer_name);

  return (
    <div className="container mt-4">
      {/* Privacy Notice - Only show when there's PHI data (like mileage page) */}
      {hasPhiData && showPrivacyNotice && (
        <JobLogPrivacyNotice onDismiss={handleDismissPrivacyNotice} />
      )}

      {alert.show && (
        <Alert
          variant={alert.variant}
          onClose={() => setAlert({ ...alert, show: false })}
          dismissible
        >
          {alert.message}
        </Alert>
      )}

      <Tabs
        activeKey={key}
        onSelect={(k) => {
          setKey(k);
          if (k === "form") setEditingLog(null);
        }}
        className="mb-3"
      >
        <Tab eventKey="list" title="Job Log List">
          {/* PHI Data Indicator (exactly like mileage page) */}
          {hasPhiData && (
            <div className="text-muted small mb-3">
              <i className="bi bi-info-circle me-1"></i>
              Logs marked with <span className="badge bg-info">PHI</span>{" "}
              contain Protected Health Information and all access is logged for
              HIPAA compliance.
            </div>
          )}

          {/* Filter section */}
          <div className="row mb-3">
            <div className="col-md-3">
              <label htmlFor="start_date">Start Date</label>
              <input
                id="start_date"
                type="date"
                className="form-control"
                value={filters.start_date}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    start_date: e.target.value,
                  }))
                }
              />
            </div>
            <div className="col-md-3">
              <label htmlFor="end_date">End Date</label>
              <input
                id="end_date"
                type="date"
                className="form-control"
                value={filters.end_date}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, end_date: e.target.value }))
                }
              />
            </div>
            <div className="col-md-2">
              <label htmlFor="billable_status">Billable Status</label>
              <select
                id="billable_status"
                className="form-control"
                value={filters.is_billable}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    is_billable: e.target.value,
                  }))
                }
              >
                <option value="">All</option>
                <option value="true">Billable</option>
                <option value="false">Non-Billable</option>
              </select>
            </div>

            {/* Consumer Search */}
            <div className="col-md-2">
              <label htmlFor="consumer_search">
                Consumer{" "}
                <span className="badge bg-info" style={{ fontSize: "0.5em" }}>
                  PHI
                </span>
              </label>
              <input
                id="consumer_search"
                type="text"
                className="form-control"
                placeholder="Search consumers..."
                value={filters.consumer_search}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    consumer_search: e.target.value,
                  }))
                }
              />
            </div>

            {/* Job Coach Search */}
            <div className="col-md-2">
              <label htmlFor="coach_search">Job Coach</label>
              <input
                id="coach_search"
                type="text"
                className="form-control"
                placeholder="Search coach..."
                value={filters.coach_search}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    coach_search: e.target.value,
                  }))
                }
              />
            </div>

            {/* Authorization Search */}
            <div className="col-md-2">
              <label htmlFor="authorization_search">Authorization</label>
              <input
                id="authorization_search"
                type="text"
                className="form-control"
                placeholder="Search auth #..."
                value={filters.authorization_search}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    authorization_search: e.target.value,
                  }))
                }
              />
            </div>

            <div className="col-md-2 d-flex align-items-end gap-2">
              <button
                className="btn btn-primary flex-fill"
                onClick={fetchJobLogs}
              >
                Search
              </button>
              <button
                className="btn btn-secondary"
                onClick={() =>
                  setFilters({
                    start_date: "",
                    end_date: "",
                    is_paid: "",
                    is_billable: "",
                    consumer_search: "",
                    authorization_search: "",
                    coach_search: "",
                  })
                }
              >
                Clear
              </button>
            </div>
          </div>

          {(filters.start_date ||
            filters.end_date ||
            filters.is_billable ||
            filters.consumer_search ||
            filters.authorization_search ||
            filters.coach_search) && (
            <div className="mb-3">
              <div className="text-muted mb-2">
                <i className="bi bi-funnel-fill me-1"></i> Active Filters:
              </div>
              <div className="d-flex flex-wrap gap-1">
                {filters.start_date && (
                  <span className="badge bg-light text-dark">
                    From: {filters.start_date}
                    <button
                      className="btn-close btn-close-sm ms-1"
                      onClick={() =>
                        setFilters((prev) => ({ ...prev, start_date: "" }))
                      }
                    ></button>
                  </span>
                )}
                {filters.end_date && (
                  <span className="badge bg-light text-dark">
                    To: {filters.end_date}
                    <button
                      className="btn-close btn-close-sm ms-1"
                      onClick={() =>
                        setFilters((prev) => ({ ...prev, end_date: "" }))
                      }
                    ></button>
                  </span>
                )}
                {filters.consumer_search && (
                  <span className="badge bg-info text-white">
                    Consumer: {filters.consumer_search}
                    <button
                      className="btn-close btn-close-white btn-close-sm ms-1"
                      onClick={() =>
                        setFilters((prev) => ({ ...prev, consumer_search: "" }))
                      }
                    ></button>
                  </span>
                )}
                {filters.coach_search && (
                  <span className="badge bg-primary text-white">
                    Job Coach: {filters.coach_search}
                    <button
                      className="btn-close btn-close-white btn-close-sm ms-1"
                      onClick={() =>
                        setFilters((prev) => ({ ...prev, coach_search: "" }))
                      }
                    ></button>
                  </span>
                )}
                {filters.authorization_search && (
                  <span className="badge bg-warning text-dark">
                    Auth: {filters.authorization_search}
                    <button
                      className="btn-close btn-close-sm ms-1"
                      onClick={() =>
                        setFilters((prev) => ({
                          ...prev,
                          authorization_search: "",
                        }))
                      }
                    ></button>
                  </span>
                )}
                {filters.is_billable && (
                  <span className="badge bg-success">
                    Status:{" "}
                    {filters.is_billable === "true"
                      ? "Billable"
                      : "Non-billable"}
                    <button
                      className="btn-close btn-close-white btn-close-sm ms-1"
                      onClick={() =>
                        setFilters((prev) => ({ ...prev, is_billable: "" }))
                      }
                    ></button>
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Record Count and PDF Button */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="text-muted">
              Showing <strong>{jobLogs.length}</strong> record
              {jobLogs.length !== 1 ? "s" : ""}
              {(filters.start_date ||
                filters.end_date ||
                filters.is_billable ||
                filters.coach_search ||
                filters.consumer_search ||
                filters.authorization_search) && <span> (filtered)</span>}
            </div>
            <button
              className="btn btn-outline-primary"
              onClick={handleGeneratePDF}
              title="Generate PDF report of selected job logs"
            >
              <i className="bi bi-file-earmark-pdf me-2"></i>
              Generate Job Log PDF
            </button>
          </div>

          {/* Responsive table/list */}
          <>
            {/* Desktop/tablet table view */}
            <div className="table-responsive d-none d-md-block">
              <table className="table table-bordered">
                <thead className="table-light">
                  <tr>
                    <th scope="col">
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedLogIds(
                              jobLogs.map((log) => log.job_log_id)
                            );
                          } else {
                            setSelectedLogIds([]);
                          }
                        }}
                        checked={
                          selectedLogIds.length === jobLogs.length &&
                          jobLogs.length > 0
                        }
                        aria-label="Select all logs"
                      />
                    </th>
                    <th scope="col">Date</th>
                    <th scope="col">Consumer</th>
                    <th scope="col">User</th>
                    <th scope="col">Authorization</th>
                    <th scope="col">Hours</th>
                    <th scope="col">Type</th>
                    <th scope="col">Paid</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {jobLogs.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center text-muted py-4">
                        No job logs found. Click "Add Job Log" to create your
                        first entry.
                      </td>
                    </tr>
                  ) : (
                    jobLogs.map((log) => (
                      <tr key={log.job_log_id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedLogIds.includes(log.job_log_id)}
                            onChange={() => toggleSelectLog(log.job_log_id)}
                            aria-label={`Select log for ${
                              log.consumer_name || "Non-billable"
                            } on ${formatDate(log.date_of_service)}`}
                          />
                        </td>
                        <td>
                          {formatDate(log.date_of_service)}
                          {log.consumer_name && (
                            <span
                              className="badge bg-info ms-2"
                              title="Contains Protected Health Information"
                            >
                              PHI
                            </span>
                          )}
                        </td>
                        <td>{log.consumer_name || "-"}</td>
                        <td>{log.user_name}</td>
                        <td>
                          <div>{log.authorization_number || "-"}</div>
                          {log.service_name && (
                            <small className="text-muted d-block">{log.service_name}</small>
                          )}
                          {log.flat_rate && log.milestone_name && (
                            <small className="d-block">
                              <span 
                                className={log.milestone_invoiced ? "text-success" : "text-primary"}
                                title={log.milestone_invoiced ? "Invoiced" : "In Progress"}
                              >
                                {log.milestone_name}
                                {log.milestone_invoiced && (
                                  <i className="bi bi-lock-fill ms-1" style={{ fontSize: '0.75em' }}></i>
                                )}
                              </span>
                            </small>
                          )}
                        </td>
                        <td>{log.hours_worked}</td>
                        <td>
                          <span
                            className={`badge ${
                              log.is_billable !== false
                                ? "bg-primary"
                                : "bg-secondary"
                            }`}
                          >
                            {log.is_billable !== false
                              ? "Billable"
                              : "Non-Billable"}
                          </span>
                        </td>
                        <td>
                          {log.is_paid ? (
                            <span className="text-success">
                              <i className="bi bi-check-circle me-1"></i>Yes
                            </span>
                          ) : (
                            <span className="text-danger">
                              <i className="bi bi-x-circle me-1"></i>No
                            </span>
                          )}
                        </td>
                        <td>
                          {!log.is_paid && (
                            <>
                              <button
                                className="btn btn-sm btn-primary me-2"
                                onClick={async () => {
                                  try {
                                    const res = await secureAxios.get(
                                      `/api/job-logs/${log.job_log_id}`
                                    );
                                    setEditingLog(res.data);
                                    setKey("form");
                                  } catch (err) {
                                    console.error(
                                      "[X] Failed to load full log:",
                                      err
                                    );
                                    setAlert({
                                      show: true,
                                      message:
                                        "Failed to load log details for editing.",
                                      variant: "danger",
                                    });
                                  }
                                }}
                                aria-label={`Edit log for ${
                                  log.consumer_name || "Non-billable"
                                }`}
                                title="Edit this job log"
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDelete(log.job_log_id)}
                                aria-label={`Delete log for ${
                                  log.consumer_name || "Non-billable"
                                }`}
                                title="Delete this job log"
                              >
                                Delete
                              </button>
                            </>
                          )}
                          {log.is_paid && (
                            <span className="text-muted small">
                              <i className="bi bi-lock me-1"></i>Locked
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile-friendly stacked list view */}
            <ul className="list-group d-block d-md-none">
              {jobLogs.length === 0 ? (
                <li className="list-group-item text-center text-muted py-4">
                  No job logs found. Click "Add Job Log" to create your first
                  entry.
                </li>
              ) : (
                jobLogs.map((log) => (
                  <li key={log.job_log_id} className="list-group-item">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`log-check-${log.job_log_id}`}
                          checked={selectedLogIds.includes(log.job_log_id)}
                          onChange={() => toggleSelectLog(log.job_log_id)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`log-check-${log.job_log_id}`}
                        >
                          {formatDate(log.date_of_service)}
                        </label>
                      </div>
                      {log.consumer_name && (
                        <span
                          className="badge bg-info"
                          title="Contains Protected Health Information"
                        >
                          PHI
                        </span>
                      )}
                    </div>

                    <div>
                      <strong>Consumer:</strong> {log.consumer_name || "-"}
                    </div>
                    <div>
                      <strong>User:</strong> {log.user_name}
                    </div>
                    <div>
                      <strong>Auth #:</strong>{" "}
                      {log.authorization_number || "-"}
                    </div>
                    <div>
                      <strong>Service:</strong> {log.service_name || "-"}
                    </div>
                    {log.flat_rate && (
                      <div>
                        <strong>Milestone:</strong>{" "}
                        {log.milestone_name ? (
                          <span 
                            className={log.milestone_invoiced ? "text-success" : "text-primary"}
                          >
                            {log.milestone_name}
                            {log.milestone_invoiced && (
                              <i className="bi bi-lock-fill ms-1" style={{ fontSize: '0.75em' }}></i>
                            )}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </div>
                    )}
                    <div>
                      <strong>Hours:</strong> {log.hours_worked}
                    </div>
                    <div>
                      <strong>Type:</strong>{" "}
                      <span
                        className={`badge ${
                          log.is_billable !== false
                            ? "bg-primary"
                            : "bg-secondary"
                        }`}
                      >
                        {log.is_billable !== false
                          ? "Billable"
                          : "Non-Billable"}
                      </span>
                    </div>
                    <div>
                      <strong>Paid:</strong>{" "}
                      {log.is_paid ? (
                        <span className="text-success">Yes</span>
                      ) : (
                        <span className="text-danger">No</span>
                      )}
                    </div>

                    {!log.is_paid && (
                      <div className="mt-2 d-flex gap-2">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={async () => {
                            try {
                              const res = await secureAxios.get(
                                `/api/job-logs/${log.job_log_id}`
                              );
                              setEditingLog(res.data);
                              setKey("form");
                            } catch (err) {
                              console.error("[X] Failed to load full log:", err);
                              setAlert({
                                show: true,
                                message:
                                  "Failed to load log details for editing.",
                                variant: "danger",
                              });
                            }
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(log.job_log_id)}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                    {log.is_paid && (
                      <div className="mt-2">
                        <span className="text-muted small">
                          <i className="bi bi-lock me-1"></i>
                          This log is locked and cannot be edited
                        </span>
                      </div>
                    )}
                  </li>
                ))
              )}
            </ul>
          </>
        </Tab>

        <Tab
          eventKey="form"
          title={editingLog ? "Edit Job Log" : "Add Job Log"}
        >
          <JobLogForm
            key={editingLog?.job_log_id || "new"}
            initialData={editingLog || {}}
            onSuccess={handleJobLogSaved}
            onCancel={() => {
              setEditingLog(null);
              setPrefillData(null);
              setKey("list");
            }}
            prefillConsumerId={prefillData?.consumer_id}
            prefillDate={prefillData?.date}
            prefillUserId={prefillData?.user_id}
          />
        </Tab>
      </Tabs>

      {/* HIPAA Footer */}
      <HIPAAFooter />
    </div>
  );
};

export default JobLogPage;